import { create } from 'zustand';
import type {
  ExtensionManifest,
  ExtensionState,
  RegisteredExtension,
  PanelContribution,
  CommandContribution,
  ToolbarItemContribution,
  StatusBarItemContribution,
  DeployTargetContribution,
  LifecycleHookContribution,
} from '@/extensions/types';

/* ================================================================== */
/*  Flattened contribution with extension ownership                    */
/* ================================================================== */

export interface OwnedPanel extends PanelContribution {
  extensionId: string;
}

export interface OwnedCommand extends CommandContribution {
  extensionId: string;
}

export interface OwnedToolbarItem extends ToolbarItemContribution {
  extensionId: string;
}

export interface OwnedStatusBarItem extends StatusBarItemContribution {
  extensionId: string;
}

export interface OwnedDeployTarget extends DeployTargetContribution {
  extensionId: string;
}

export interface OwnedLifecycleHook extends LifecycleHookContribution {
  extensionId: string;
}

/* ================================================================== */
/*  Extension Store                                                   */
/* ================================================================== */

interface ExtensionStoreState {
  /** All registered extensions keyed by id */
  extensions: Map<string, RegisteredExtension>;

  /* -- Flattened contribution indexes for O(1) lookups ------------- */
  panels: OwnedPanel[];
  commands: OwnedCommand[];
  toolbarItems: OwnedToolbarItem[];
  statusBarItems: OwnedStatusBarItem[];
  deployTargets: OwnedDeployTarget[];
  lifecycleHooks: OwnedLifecycleHook[];

  /* -- Actions ----------------------------------------------------- */
  registerExtension: (manifest: ExtensionManifest) => void;
  setExtensionState: (id: string, state: ExtensionState, error?: string) => void;
  unregisterExtension: (id: string) => void;
  getExtension: (id: string) => RegisteredExtension | undefined;

  /** Rebuild flattened indexes from registered extensions */
  rebuildContributions: () => void;
}

export const useExtensionStore = create<ExtensionStoreState>((set, get) => ({
  extensions: new Map(),
  panels: [],
  commands: [],
  toolbarItems: [],
  statusBarItems: [],
  deployTargets: [],
  lifecycleHooks: [],

  registerExtension: (manifest) => {
    const extensions = new Map(get().extensions);
    extensions.set(manifest.id, {
      manifest,
      state: 'installed',
      storageData: {},
    });
    set({ extensions });
    get().rebuildContributions();
  },

  setExtensionState: (id, state, error) => {
    const extensions = new Map(get().extensions);
    const ext = extensions.get(id);
    if (!ext) return;
    extensions.set(id, { ...ext, state, error });
    set({ extensions });
  },

  unregisterExtension: (id) => {
    const extensions = new Map(get().extensions);
    extensions.delete(id);
    set({ extensions });
    get().rebuildContributions();
  },

  getExtension: (id) => {
    return get().extensions.get(id);
  },

  rebuildContributions: () => {
    const panels: OwnedPanel[] = [];
    const commands: OwnedCommand[] = [];
    const toolbarItems: OwnedToolbarItem[] = [];
    const statusBarItems: OwnedStatusBarItem[] = [];
    const deployTargets: OwnedDeployTarget[] = [];
    const lifecycleHooks: OwnedLifecycleHook[] = [];

    for (const [extId, ext] of get().extensions) {
      const c = ext.manifest.contributes;

      if (c.panels) {
        for (const p of c.panels) {
          panels.push({ ...p, extensionId: extId });
        }
      }
      if (c.commands) {
        for (const cmd of c.commands) {
          commands.push({ ...cmd, extensionId: extId });
        }
      }
      if (c.toolbarItems) {
        for (const t of c.toolbarItems) {
          toolbarItems.push({ ...t, extensionId: extId });
        }
      }
      if (c.statusBarItems) {
        for (const s of c.statusBarItems) {
          statusBarItems.push({ ...s, extensionId: extId });
        }
      }
      if (c.deployTargets) {
        for (const d of c.deployTargets) {
          deployTargets.push({ ...d, extensionId: extId });
        }
      }
      if (c.lifecycleHooks) {
        for (const h of c.lifecycleHooks) {
          lifecycleHooks.push({ ...h, extensionId: extId });
        }
      }
    }

    // Sort panels and toolbar items by priority
    panels.sort((a, b) => (a.priority ?? 50) - (b.priority ?? 50));
    toolbarItems.sort((a, b) => (a.priority ?? 100) - (b.priority ?? 100));
    statusBarItems.sort((a, b) => (a.priority ?? 50) - (b.priority ?? 50));

    set({ panels, commands, toolbarItems, statusBarItems, deployTargets, lifecycleHooks });
  },
}));
